import { from, Observable, of, throwError } from "rxjs";
import { inject, Injectable } from "@angular/core";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import { last } from "lodash-es";
import { Mutex } from "async-mutex";
import { catchError, finalize, map, switchMap, tap } from "rxjs/operators";
import { CacheService } from "src/app/services/cache.service";
import { HTTP_CONTEXT_TOKEN_NO_CACHE } from "src/app/core/http-context-tokens";

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private readonly cacheService = inject(CacheService);

  private mutexMap: Map<string, Mutex> = new Map<string, Mutex>();

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const url = req.url;

    if (!this.cacheService.isEnabled()) {
      return next.handle(req);
    }

    switch (req.method.toLowerCase()) {
      case "get": {
        if (!this.isEntityRequest(url) || this.isNoCacheRequest(req)) {
          return next.handle(this.removeNoCacheHeader(req));
        }

        return this.handleReadRequest(req, next);
      }

      case "put":
      case "delete":
      case "patch":
      case "post": {
        return this.handleWriteRequest(req, next);
      }
      default: {
        return next.handle(req);
      }
    }
  }

  private isNoCacheRequest(req: HttpRequest<any>): boolean {
    return (
      req.headers.has("snt-no-cache") ||
      req.context?.get(HTTP_CONTEXT_TOKEN_NO_CACHE)
    );
  }

  private removeNoCacheHeader(req: HttpRequest<any>): HttpRequest<any> {
    return req.clone({ headers: req.headers.delete("snt-no-cache") });
  }

  private handleReadRequest(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const cacheKey = this.getEntityIdByUrl(req);

    if (this.cacheService.has(cacheKey)) {
      return of(this.cacheService.get(cacheKey)!.value.clone());
    }

    let mutex: Mutex | undefined;

    if (this.mutexMap.has(cacheKey)) {
      mutex = this.mutexMap.get(cacheKey);

      return from(mutex!.waitForUnlock()).pipe(
        map(() => {
          return this.cacheService.get(cacheKey)!.value.clone();
        }),
        catchError((err) => {
          return throwError(() => err);
        })
      );
    } else {
      mutex = new Mutex();

      this.mutexMap.set(cacheKey, mutex);

      const mutexAcquirePromise = mutex.acquire();

      return from(mutexAcquirePromise).pipe(
        switchMap((mutexReleaser) => {
          return next.handle(req).pipe(
            tap((stateEvent) => {
              if (stateEvent instanceof HttpResponse) {
                this.cacheService.set(cacheKey, {
                  timestamp: new Date(),
                  value: stateEvent,
                  lastUpdate: undefined
                });
              }
            }),
            catchError((error) => {
              this.cacheService.delete(cacheKey);
              mutex?.cancel();
              return throwError(() => error);
            }),
            finalize(() => {
              if (mutex?.isLocked()) {
                mutexReleaser();
              }
              this.mutexMap.delete(cacheKey);
            })
          );
        })
      );
    }
  }

  private handleWriteRequest(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const cacheKeys = this.getEntitiesIdsByUrl(req.url);

    for (const cacheKey of cacheKeys) {
      this.cacheService.delete(cacheKey);
    }

    return next.handle(req);
  }

  private readonly idRegex: RegExp = /^[0-9a-fA-F]{24}$/;

  private getEntityIdByUrl(request: HttpRequest<any>): string {
    const tokens = request.url.split("/");
    const tokenId = tokens[tokens.length - 1];
    return this.cacheService.getKey(tokenId, request.params);
  }

  private getEntitiesIdsByUrl(url: string): string[] {
    const tokens = url.split("/");
    return tokens
      .filter((x) => this.idRegex.test(x))
      .map((token) => this.cacheService.getKey(token));
  }

  private isEntityRequest(url: string): boolean {
    const tokens = url.split("/");
    const idToTest = last(tokens);
    return idToTest ? this.idRegex.test(idToTest) : false;
  }
}
